import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Phone, MessageCircle, Send, Baby } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Calendar as CalendarComponent } from "./ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { format } from "date-fns";
import { pt } from "date-fns/locale";

const FormularioMarcacao = () => {
  const [motivo, setMotivo] = useState("");
  const [horario, setHorario] = useState("");
  const [data, setData] = useState(null);
  const [telefone, setTelefone] = useState("");

  const motivosConsulta = [
    "Consulta de Rotina",
    "Implantes Dentários",
    "Ortodontia / Alinhadores",
    "Branqueamento Dentário",
    "Dor de Dentes",
    "Urgência Dentária",
    "Primeira Consulta",
    "Medicina Dentária Infantil",
    "Outro",
  ];

  const horariosDisponiveis = [
    "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
    "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
    "17:00", "17:30", "18:00", "18:30"
  ];

  const enviarWhatsApp = () => {
    const dataFormatada = data ? format(data, "dd/MM/yyyy", { locale: pt }) : "A definir";
    const mensagem = `Olá! Gostaria de fazer uma marcação:

📋 *Motivo:* ${motivo || "Não especificado"}
📅 *Data Preferencial:* ${dataFormatada}
🕐 *Horário Preferencial:* ${horario || "A definir"}
📞 *Telefone:* ${telefone || "Não indicado"}

Aguardo confirmação. Obrigado!`;

    const urlWhatsApp = `https://wa.me/351927525885?text=${encodeURIComponent(mensagem)}`;
    window.open(urlWhatsApp, "_blank");
  };

  return (
    <section
      id="marcacao"
      className="py-16 md:py-24 bg-white"
      data-testid="marcacao-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-xs font-semibold tracking-[0.15em] uppercase text-[#8A8A8A] block mb-4">
            Agende a Sua Consulta
          </span>
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-[#1A1A1A] tracking-tight mb-4">
            Marcação Rápida via WhatsApp
          </h2>
          <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">
            Preencha os campos abaixo e envie diretamente para o nosso WhatsApp. Responderemos o mais breve possível.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-2xl mx-auto bg-[#FAFAFA] border border-gray-100 rounded-3xl p-8 md:p-10"
        >
          <div className="space-y-6">
            {/* Motivo da Marcação */}
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                <MessageCircle className="w-4 h-4 inline mr-2" />
                Motivo da Marcação
              </label>
              <Select onValueChange={setMotivo} value={motivo}>
                <SelectTrigger className="w-full h-12 bg-white border-gray-200" data-testid="motivo-select">
                  <SelectValue placeholder="Selecione o motivo da consulta" />
                </SelectTrigger>
                <SelectContent>
                  {motivosConsulta.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Data Preferencial */}
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Data Preferencial
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-12 justify-start text-left font-normal bg-white border-gray-200"
                    data-testid="data-button"
                  >
                    {data ? (
                      format(data, "PPP", { locale: pt })
                    ) : (
                      <span className="text-gray-500">Selecione uma data</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={data}
                    onSelect={setData}
                    disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                    initialFocus
                    locale={pt}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Horário Preferencial */}
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                <Clock className="w-4 h-4 inline mr-2" />
                Horário Preferencial
              </label>
              <Select onValueChange={setHorario} value={horario}>
                <SelectTrigger className="w-full h-12 bg-white border-gray-200" data-testid="horario-select">
                  <SelectValue placeholder="Selecione o horário pretendido" />
                </SelectTrigger>
                <SelectContent>
                  {horariosDisponiveis.map((h) => (
                    <SelectItem key={h} value={h}>
                      {h}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Telefone de Contacto */}
            <div>
              <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Nº de Telefone de Contacto
              </label>
              <Input
                type="tel"
                placeholder="Ex: 912 345 678"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                className="w-full h-12 bg-white border-gray-200"
                data-testid="telefone-input"
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={enviarWhatsApp}
              className="w-full h-14 bg-[#1A1A1A] hover:bg-[#333333] text-white rounded-full text-lg font-semibold transition-all"
              data-testid="enviar-whatsapp-btn"
            >
              <Send className="w-5 h-5 mr-2" />
              Enviar Marcação via WhatsApp
            </Button>
          </div>
        </motion.div>

        {/* Medicina Dentária Infantil Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-[#1A1A1A] rounded-2xl p-8 md:p-10 text-white"
          data-testid="medicina-infantil-banner"
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Baby className="w-8 h-8 text-white" />
            </div>
            <div className="text-center md:text-left">
              <h3 className="font-outfit text-xl md:text-2xl font-semibold mb-2">
                Medicina Dentária Infantil
              </h3>
              <p className="text-white/80 text-lg">
                Porque investir na saúde oral dos mais pequenos é investir no futuro.
              </p>
            </div>
            <a
              href="tel:232088790"
              className="mt-4 md:mt-0 md:ml-auto bg-white text-[#1A1A1A] rounded-full px-8 py-3 font-semibold hover:bg-gray-100 transition-all flex-shrink-0"
            >
              Marcar Consulta
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FormularioMarcacao;
